import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useGlobalContext } from '../../../context/GlobalContext';
import { getOwnerDashboardAction } from '../../../actions/questionsActions';
import { getNotifsAction } from '../../../actions/authActions';
import styles from './OwnerDashboard.module.scss';

const STATUS_COLORS = ['#FB6868', '#FF972B', '#85CE59'];
const TAG_COLORS = ['#FB6868', '#85CE59', '#DADADF'];
const AREA_COLORS = ['#44CDEB', '#F59F1A', '#5A8FCA', '#C4CAD8'];
const VIEW_OPTIONS = [
  { value: 'month', label: 'Vue mois' },
  { value: 'week', label: 'Vue semaine' },
];

function getEmptyDashboard() {
  return {
    sourceCounts: [],
    statusCounts: {
      assigned: 0,
      submitted: 0,
      resolved: 0,
    },
    topTags: [],
    therapeuticAreaSeriesByMonth: {
      periods: [],
      series: [],
    },
    therapeuticAreaSeriesByWeek: {
      periods: [],
      series: [],
    },
  };
}

function getChartData(seriesGroup) {
  if (!seriesGroup?.periods?.length) {
    return [];
  }

  return seriesGroup.periods.map((period, index) => {
    const point = {
      label: period.label,
    };

    seriesGroup.series.forEach((serie) => {
      point[serie.label] = serie.values[index]?.value || 0;
    });

    return point;
  });
}

function StatusRing({ count, label, color }) {
  return (
    <Link to="/question-assign" className={styles.statusRing} style={{ borderColor: color }}>
      <p className={styles.statusCount}>{count}</p>
      <p className={styles.statusLabel}>{label}</p>
    </Link>
  );
}

export default function OwnerDashboard() {
  const [context, dispatch] = useGlobalContext();
  const { userAuth } = context.auth;
  const [dashboard, setDashboard] = useState(getEmptyDashboard());
  const [viewMode, setViewMode] = useState('month');

  const firstName = userAuth?.firstName || userAuth?.profile?.firstName || '';

  const loadDashboard = useCallback(async () => {
    try {
      await getNotifsAction(dispatch, 'Medocta');
    } catch (error) {}

    const ownerDashboard = await getOwnerDashboardAction(dispatch);
    setDashboard(ownerDashboard || getEmptyDashboard());
  }, [dispatch]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const lineChartData = useMemo(() => {
    return getChartData(
      viewMode === 'week'
        ? dashboard.therapeuticAreaSeriesByWeek
        : dashboard.therapeuticAreaSeriesByMonth,
    );
  }, [dashboard, viewMode]);

  const tagTotal = useMemo(() => (
    dashboard.topTags.reduce((sum, tag) => sum + tag.value, 0)
  ), [dashboard.topTags]);

  return (
    <div className={`page-container ${styles.container}`}>
      <h1 className={styles.title}>Bonjour {firstName}</h1>

      <div className={styles.grid}>
        <section className={`${styles.card} ${styles.sourceCard}`}>
          <p className={styles.cardTitle}>Source de mes questions</p>
          <div className={styles.barChart}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dashboard.sourceCounts} margin={{ top: 26, right: 10, left: -15, bottom: 0 }}>
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#192243' }} />
                <YAxis hide />
                <Tooltip />
                <Bar dataKey="value" radius={[5, 5, 0, 0]}>
                  {dashboard.sourceCounts.map((entry, index) => (
                    <Cell
                      key={entry.label}
                      fill={['#02C0F0', '#3D8FBC', '#DE4F36', '#9A44C1', '#D9D9D9', '#F4A11A'][index] || '#192243'}
                    />
                  ))}
                  <LabelList dataKey="value" position="top" fill="#192243" fontSize={16} fontFamily="Anybody-SemiBold" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className={`${styles.card} ${styles.statusCard}`}>
          <div className={styles.statusGrid}>
            <StatusRing count={dashboard.statusCounts.assigned} label="Questions assignées" color={STATUS_COLORS[0]} />
            <StatusRing count={dashboard.statusCounts.submitted} label="Questions à envoyer en validation" color={STATUS_COLORS[1]} />
            <StatusRing count={dashboard.statusCounts.resolved} label="Questions envoyées" color={STATUS_COLORS[2]} />
          </div>
        </section>

        <section className={`${styles.card} ${styles.donutCard}`}>
          <p className={styles.cardTitle}>Type de mes questions</p>
          <div className={styles.donutWrap}>
            <div className={styles.donutShell}>
              <div className={styles.badges}>
                {dashboard.topTags.map((tag, index) => (
                  <div key={tag.label} className={`${styles.badge} ${styles[`badge${index + 1}`]}`}>
                    {tag.value}
                  </div>
                ))}
              </div>
              <div className={styles.donutChart}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={dashboard.topTags}
                      dataKey="value"
                      nameKey="label"
                      innerRadius={48}
                      outerRadius={74}
                      stroke="none"
                      startAngle={30}
                      endAngle={390}
                      paddingAngle={4}
                    >
                      {dashboard.topTags.map((tag, index) => (
                        <Cell key={tag.label} fill={TAG_COLORS[index] || '#DADADF'} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className={styles.donutCenter}>{tagTotal}</div>
              </div>
            </div>
            <div className={styles.legend}>
              {dashboard.topTags.map((tag, index) => (
                <div key={tag.label} className={styles.legendItem}>
                  <span className={styles.legendDot} style={{ backgroundColor: TAG_COLORS[index] || '#DADADF' }} />
                  <p>{tag.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={`${styles.card} ${styles.timelineCard}`}>
          <div className={styles.timelineHeader}>
            <p className={styles.cardTitle}>Toutes les questions médicales sur l’année par aire thérapeutique</p>
            <div className={styles.switcher}>
              {VIEW_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={viewMode === option.value ? styles.active : ''}
                  onClick={() => setViewMode(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.areaChart}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={lineChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  {AREA_COLORS.map((color, index) => (
                    <linearGradient key={color} id={`ownerGradient${index}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={color} stopOpacity={0.28} />
                      <stop offset="95%" stopColor={color} stopOpacity={0.04} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid vertical={false} stroke="#E8EAF0" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#A0A4B0' }} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#A0A4B0' }} />
                <Tooltip />
                {(
                  viewMode === 'week'
                    ? dashboard.therapeuticAreaSeriesByWeek.series
                    : dashboard.therapeuticAreaSeriesByMonth.series
                ).map((serie, index) => (
                  <Area
                    key={serie.label}
                    type="monotone"
                    dataKey={serie.label}
                    stroke={AREA_COLORS[index] || '#192243'}
                    fill={`url(#ownerGradient${index})`}
                    strokeWidth={2}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className={styles.timelineLegend}>
            {(
              viewMode === 'week'
                ? dashboard.therapeuticAreaSeriesByWeek.series
                : dashboard.therapeuticAreaSeriesByMonth.series
            ).map((serie, index) => (
              <div key={serie.label} className={styles.legendItem}>
                <span className={styles.legendDot} style={{ backgroundColor: AREA_COLORS[index] || '#192243' }} />
                <p>{serie.label}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
